'use client'

import Link from 'next/link'
import type { ReactNode } from 'react'
import { buildFareharborBookingUrl, parseFareharborCtaHref } from '@/lib/fareharbor'
import { useFareharbor } from './FareharborContext'
import { track } from '@/lib/analytics'

/**
 * Best-effort label for the funnel event. The CTA's children are usually a
 * plain string (the button copy); fall back to a generic label otherwise so
 * we never crash the click path on a non-string node.
 */
function labelOf(children: ReactNode): string {
  return typeof children === 'string' ? children : 'Book'
}

/**
 * Drop-in replacement for a CTA `<Link>` that understands the `fareharbor:`
 * sentinel href. For a normal href it renders an ordinary next/link, so it can
 * replace any `<Link href className>{label}</Link>` one-for-one. For a
 * `fareharbor:` href it renders a plain `<a>` pointing at the tenant's
 * FareHarbor booking page.
 *
 * We deliberately do NOT call `FH.open()` or `preventDefault` the anchor.
 * FareHarbor's `autolightframe` script (loaded once in the root layout) installs
 * a document-level delegated click listener that matches any anchor whose href
 * points at `fareharbor.com/embeds/book/...` and opens the booking flow as a
 * lightbox overlay. Delegation means it works for server-rendered AND
 * React-rendered anchors alike — no per-anchor binding, no `FH.open()` call
 * needed. An earlier `FH.open()` handler silently failed in FareHarbor's SSR kit
 * and `preventDefault`'d the anchor, leaving the button dead — so a handler that
 * cancels the event is forbidden. A *non-preventing*, fire-and-forget analytics
 * `onClick` (used below for funnel emit) is safe: it runs alongside the native
 * delegated listener without cancelling it. With the script absent / JS off, the
 * bare href navigates to the booking page as a graceful fallback.
 *
 * Tenant FareHarbor config (enabled + shortname) comes from FareharborContext,
 * provided once at the layout level — no per-section tenant threading.
 *
 * `bindAttrs` forwards SiteSmith inline-edit data attributes (e.g.
 * `data-field`) so a rewired booking CTA stays editable in the visual editor.
 */
export function BookingCta({
  href,
  className,
  children,
  bindAttrs,
}: {
  href: string
  className?: string
  children: ReactNode
  bindAttrs?: Record<string, string>
}) {
  const { enabled, shortname } = useFareharbor()
  const parsed = parseFareharborCtaHref(href)

  if (!parsed) {
    // Plain (non-FareHarbor) CTA. Fire section_cta_click for the funnel; the
    // navigation is untouched (track is fire-and-forget, never preventDefault).
    return (
      <Link
        href={href}
        className={className}
        {...bindAttrs}
        onClick={() => track('section_cta_click', { kind: 'booking_cta', label: labelOf(children), href })}
      >
        {children}
      </Link>
    )
  }

  // Sentinel booking CTA. With FareHarbor enabled + a shortname, point the
  // anchor at the tenant's booking page; the autolightframe script upgrades the
  // click into a lightbox overlay and navigates as a fallback. Without a
  // shortname (FareHarbor not configured), fall back to the contact page.
  const isBooking = enabled && !!shortname
  const bookingHref = isBooking
    ? buildFareharborBookingUrl({ shortname, layout: 'button', flow: parsed.flow })
    : '/contact'
  const label = labelOf(children)

  // Funnel emit: always a section_cta_click; then booking_start when this
  // resolves to a real FareHarbor booking, or contact_start when it degrades
  // to the contact page (FareHarbor not configured). Fire-and-forget — the
  // anchor's existing navigation / autolightframe behavior is untouched.
  function onCtaClick() {
    track('section_cta_click', { kind: 'booking_cta', label, href: bookingHref })
    track(isBooking ? 'booking_start' : 'contact_start', { label, href: bookingHref })
  }

  return (
    <a href={bookingHref} className={className} {...bindAttrs} onClick={onCtaClick}>
      {children}
    </a>
  )
}
